import { observable, action } from 'mobx';
import { Api, CurrentEnv} from 'services/Api';
import stores from './index';
import axios from 'axios';
var FileDownload = require('js-file-download');

const EMPTY_EQUIPMENT = {}

// What "Edit details" may change (PUT /equipments/:id). Never the whole
// record: `images` is a has_many on the server and parts go through savePart.
export const EQUIPMENT_FIELDS = ['kind', 'make', 'eq_model', 'serial_number', 'eq_location', 'description', 'client_id', 'location_id'];

// The server refuses any update without these, even one that only adds a part.
const REQUIRED_ON_UPDATE = ['make', 'eq_model', 'serial_number'];

const PART_FIELDS = ['id', 'name', 'eq_type', 'eq_size', 'description'];

// The record holds its client and location as plain rows ({ id, name },
// { id, full_address }); the pickers show records shaped like the API's
// ({ id, attributes }).
const asRecord = (row) => {
  if (!row) return null;
  if (row.attributes) return row;
  return { id: row.id, attributes: { ...row } };
};

const pick = (source, keys) => keys.reduce((picked, key) => {
  if (source[key] !== undefined) picked[key] = source[key];
  return picked;
}, {});

// Empty form for the Add Equipment dialog, reset after each create so
// nothing (images especially) carries into the next one.
const blankEquipment = () => ({
  images: [],
  equipment_parts_attributes: []
});

class EquipmentStore {

  @observable showUploadAttachmentLoader = false;
  @observable isSavingEquipment = false;   // Edit details, add/edit a part
  @observable deletingPartId = null;

  // The work orders an equipment was serviced on (its page).
  @observable equipmentWorkReports = {
    isLoading: false,
    isError: false,
    objects: [],
    equipmentId: null,
  };

  @observable persistEquipment = {
    isLoading: false,
    isError: false,
    object: blankEquipment(),
  };

  @action
  clearPersistEquipment() {
    this.persistEquipment.object = blankEquipment();
  }

  // Fill the New equipment panel with a copy of `equipment`, to edit it.
  @action
  startEditing(equipment) {
    const a = equipment.attributes;
    this.persistEquipment.object = {
      ...blankEquipment(),
      ...pick(a, EQUIPMENT_FIELDS),
      id: equipment.id,
      client_id: a.client_id || (a.client && a.client.id) || null,
      client: asRecord(a.client),
      location_id: a.location_id || (a.location && a.location.id) || null,
      location: asRecord(a.location),
    };
  }

  @observable equipmentPart = {
    isLoading: false,
    isError: false,
    object: {},
  };

  @observable equipments = {
    isLoading: false,
    isError: false,
    objects: [],
    meta: {}
  };

  @observable clientEquipments = {
    isLoading: false,
    isError: false,
    objects: [],
    meta: {}
  };

  @observable equipment = {
    isLoading: false,
    isError: false,
    errorStatus: null,
    object: null
  };

  @observable persisteEquipment = EMPTY_EQUIPMENT;
  @observable equipmentAnalytics = {
    isLoading: false,
    isError: false,
    objects: []
  };

  @action
  loadAnalytics = (params) => {
    this.equipmentAnalytics.isLoading = true;
    let promise = Api.get(`/equipments/${params.uuid}/analytics`);

    promise
      .then((response) => {
        this.equipmentAnalytics.isLoading = false;
        this.equipmentAnalytics.isError = false;
        this.equipmentAnalytics.objects = response.data;
      })
      .catch((error) => {
        this.equipmentAnalytics.isLoading = false;
        this.equipmentAnalytics.isError = true;
      })
    
    return promise;
  }

  @action
  clearSelectedEquipment() {
    this.equipment = {
      isLoading: false,
      isError: false,
      errorStatus: null,
      object: null
    };
    this.equipmentWorkReports = { isLoading: false, isError: false, objects: [], equipmentId: null };
  }

  // The saved record comes back in full (parts included), so the page shows it
  // without a reload, as long as it's still the one on screen.
  showSaved(response) {
    const saved = response && response.data && response.data.data;
    if (saved && this.equipment.object && this.equipment.object.id === saved.id) {
      this.equipment.object = saved;
    }
  }

  @action
  saveEquipment(id, fields) {
    this.isSavingEquipment = true;
    const promise = Api.put(`/equipments/${id}`, { equipment: pick(fields, EQUIPMENT_FIELDS) });
    return promise.then(
      action((response) => { this.isSavingEquipment = false; this.showSaved(response); return response; }),
      action((error) => { this.isSavingEquipment = false; throw error; }),
    );
  }

  // Adds a part (no id) or saves one (with its id) on `equipment`.
  @action
  savePart(equipment, part) {
    this.isSavingEquipment = true;
    const fields = {
      ...pick(equipment.attributes, REQUIRED_ON_UPDATE),
      equipment_parts_attributes: [pick(part, PART_FIELDS)],
    };
    if (!fields.equipment_parts_attributes[0].id) delete fields.equipment_parts_attributes[0].id;
    const promise = Api.put(`/equipments/${equipment.id}`, { equipment: fields });
    return promise.then(
      action((response) => { this.isSavingEquipment = false; this.showSaved(response); return response; }),
      action((error) => { this.isSavingEquipment = false; throw error; }),
    );
  }

  @action
  deletePart(part) {
    this.deletingPartId = part.id;
    const promise = Api.delete('/equipments/remove_equipment_part/', { data: { id: part.id } });
    return promise.then(
      action((response) => {
        this.deletingPartId = null;
        const object = this.equipment.object;
        if (object && object.attributes.equipment_parts) {
          object.attributes.equipment_parts = object.attributes.equipment_parts.filter((item) => item.id !== part.id);
        }
        return response;
      }),
      action((error) => { this.deletingPartId = null; throw error; }),
    );
  }

  @action
  loadEquipmentWorkReports(equipmentId) {
    this.equipmentWorkReports = { isLoading: true, isError: false, objects: [], equipmentId };
    const promise = Api.get(`/equipments/${equipmentId}/work_reports`);
    promise
      .then(action((response) => {
        if (this.equipmentWorkReports.equipmentId !== equipmentId) return;
        const objects = (response.data && response.data.data) || [];
        objects.sort((a, b) => String(b.attributes.created_at).localeCompare(String(a.attributes.created_at)));
        this.equipmentWorkReports = { isLoading: false, isError: false, objects, equipmentId };
      }))
      .catch(action(() => {
        if (this.equipmentWorkReports.equipmentId !== equipmentId) return;
        this.equipmentWorkReports = { isLoading: false, isError: true, objects: [], equipmentId };
      }));
    return promise;
  }


  @action
  createEquipment() {
    this.persistEquipment.isLoading = true;
    let promise = Api.post(`/equipments/`, {equipment: this.persistEquipment.object})   

    promise
      .then((response) => {
        this.persistEquipment.isLoading = false;
        this.persistEquipment.isError = false;
        this.clearPersistEquipment();
      })
      .catch((error) => {
        this.persistEquipment.isLoading = false;
        this.persistEquipment.isError = true;       
      })

    return promise;
  }

  @action
  download_bulk_excel(searchObj) {
    let promise = Api.post('/equipments/excel_bulk_download', { search: searchObj }, { responseType: 'arraybuffer' })

    promise
      .then((response) => {
        FileDownload(response.data, 'Equipments.xls');
      })
      .catch((error) => {
      })
  }

  @action
  deleteEquipment(equipment) {
    this.equipments.isLoading = true;
    let promise = Api.delete(`/equipments/${equipment.attributes.id}`)
    
    promise
      .then((response) => {
        this.loadEquipments();
      })
      .catch((error) => {
        this.equipments.isLoading = false;
        this.equipments.isError = true;
      })
    
    return promise
  }

  @action
  deleteServicedEquipment(equipment,workOrderId) {
    this.equipments.isLoading = true;
    let data = {
      serviced_equipment_id:equipment
    }
    let promise = Api.delete('/equipments/remove_serviced_equipment/',{data})
    
    promise
      .then((response) => {
        stores.workReportStore.loadWorkReport({ uuid: workOrderId});
      })
      .catch((error) => {
        this.equipments.isLoading = false;
        this.equipments.isError = true;
      })
    
    return promise
  }


  @action
  create_serviced_equipment = (equipment_id,work_report_id) => {
    this.persistEquipment.isLoading = true;    
    let promise = Api.post('/equipments/create_serviced_equipment',{ 
      serviced_equipment: { 
        equipment_id: equipment_id,
        work_report_id: work_report_id
      }
    });
    promise
      .then((response) => {
        this.persistEquipment.isLoading = false;
        this.persistEquipment.isError = false;
        this.clearPersistEquipment();
        stores.workReportStore.loadWorkReport({ uuid: work_report_id});
        stores.equipmentStore.loadEquipmentsForWorkReports();       
      })
      .catch((error) => {
        this.persistEquipment.isLoading = false;
        this.persistEquipment.isError = true;
        console.error(error)
      })
    
    return promise;
  }


  @action
  loadEquipmentsForWorkReports = () => {
    this.equipments.isLoading = true;
    const {
      term,
      per_page,
      page,
      order,
      where
    } = stores.viewStore.equipmentSearchWorkReport;
    let promise = Api.post('/equipments/search', { 
      search: { 
        term: term,
        where: where,
        order: order,
        page: page,
        per_page: per_page
      }
    });

    promise
      .then((response) => {
        this.equipments.isLoading = false;
        this.equipments.isError = false;
        this.equipments.objects = response.data.data;
        this.equipments.meta = response.data.meta;
      })
      .catch((error) => {
        this.equipments.isLoading = false;
        this.equipments.isError = true;
        console.error(error)
      })
    
    return promise;
  }

  @action
  loadEquipments = () => {
    this.equipments.isLoading = true;
    const {
      term,
      per_page,
      page,
      order,
      where
    } = stores.viewStore.equipmentSearch;
    let promise = Api.post('/equipments/search', { 
      search: { 
        // Matches type, make, model, serial number, location and description.
        term: term || '*',
        where: where,
        order: order,
        page: page,
        per_page: per_page
      }
    });

    promise
      .then((response) => {
        this.equipments.isLoading = false;
        this.equipments.isError = false;
        this.equipments.objects = response.data.data;
        this.equipments.meta = response.data.meta;
      })
      .catch((error) => {
        this.equipments.isLoading = false;
        this.equipments.isError = true;
        console.error(error)
      })
    
    return promise;
  }

  @action
  loadClientEquipments = () => {     
    this.clientEquipments.isLoading = true;
    let promise = Api.post('/equipments/search', { 
      search: window.stores.viewStore.clientEquipmentSearch
    });

    promise
      .then((response) => {
        this.clientEquipments.isLoading = false;
        this.clientEquipments.isError = false;
        this.clientEquipments.objects = response.data.data;
        this.clientEquipments.meta = response.data.meta;
      })
      .catch((error) => {
        this.clientEquipments.isLoading = false;
        this.clientEquipments.isError = true;
        console.error(error)
      })
    
    return promise;
  }

  @action
  loadEquipment(params) {
    this.equipment.isLoading = true;
    this.equipment.isError = false;
    this.equipment.errorStatus = null;
    let promise = Api.get(`/equipments/${params.uuid}`)

    promise
      .then((response) => {
        this.equipment.isLoading = false;
        this.equipment.isError = false;
        this.equipment.object = response.data.data;
        stores.viewStore.equipmentShow(params,response.data.data.attributes.make);
      })
      .catch((error) => {
        this.equipment.isLoading = false;
        this.equipment.isError = true;
        this.equipment.errorStatus = (error && error.response && error.response.status) || null;
      })

    return promise;
  }

  @action
  uploadEquipmentAttachment(file, equipment_id = null) {
    const formData = new FormData();
    formData.append('file', file)
    formData.append('equipment_id', equipment_id)
    this.showUploadAttachmentLoader = true;
    let promise = axios.post(`${CurrentEnv.domain()}/api/v2/images`,
      formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Api-Token': stores.viewStore.currentUser.attributes.api_token,
          'X-Tenant':stores.viewStore.currentUser.attributes.tenant_id,
          'X-Client-Identifier': 'React-Web'
        }
      }
    )

    promise
      .then((response) => {
        this.showUploadAttachmentLoader = false;
      })
      .catch((error) => {
        this.showUploadAttachmentLoader = false;
      })

    return promise;

  }

  @action
  attach_image(files, equipment_id = null) {
    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('file[]',files[i])
    };
    formData.append('equipment_id', equipment_id)
    this.showUploadAttachmentLoader = true;
    let promise = axios.post(`${CurrentEnv.domain()}/api/v2/equipments/${equipment_id}/attach_image/`,
      formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Api-Token': stores.viewStore.currentUser.attributes.api_token,
          'X-Tenant':stores.viewStore.currentUser.attributes.tenant_id,
          'X-Client-Identifier': 'React-Web'
        }
      }
    )

    promise
      .then((response) => {
        this.showUploadAttachmentLoader = false;
        // let params = {
        //   uuid: equipment_id
        // }
        // this.loadEquipmentForImage(params)
        // .then((response) => {
        //   this.equipment.object = response.data.data;
        // })
      })
      .catch((error) => {
        this.showUploadAttachmentLoader = false;
      })

    return promise;

  }

  @action
  removeAttachment(attachment, equipment_id = null) {
    this.showUploadAttachmentLoader = true;
    let promise = Api.delete(`/equipments/${attachment.id}/remove_attachment`)

    promise
      .then((response) => {
        this.showUploadAttachmentLoader = false;
      })
      .catch((error) => {
        this.showUploadAttachmentLoader = false;
      })

    return promise;
  }

  @action
  loadEquipmentForImage(params) {
    let promise = Api.get(`/equipments/${params.uuid}`)
    
    promise
      .then((response) => {
        this.equipment.object = response.data.data;
      })
      .catch((error) => {
        console.error(error)
      })

    return promise;
  }

};

export default EquipmentStore;